import React, { useState, useEffect, useContext } from 'react';
import {
  Modal, Table, InputNumber, Button, Typography, Space,
  message, Drawer, Input, List, Avatar, Popconfirm, Divider, Row, Col, Pagination
} from 'antd';
import { SwapOutlined, SearchOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { LoginContext } from '../../../../context/loginContext';
import { SucursalContext } from '../../../../context/sucursalContext';

const { Text } = Typography;

const CEditarPedido = ({ visible, onClose, pedido, onGuardarCambios }) => {
  const [detallesEditados, setDetallesEditados] = useState([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [articulos, setArticulos] = useState([]);
  const [indexAReemplazar, setIndexAReemplazar] = useState(null);
  const [modoDrawer, setModoDrawer] = useState('agregar'); // 'agregar' | 'reemplazar'
  const [pagination, setPagination] = useState({ current: 1, total: 0, pageSize: 10 });
  const [nombreBusqueda, setNombreBusqueda] = useState('');
  const [saving, setSaving] = useState(false);
  const { user } = useContext(LoginContext);
  const { sucursal } = useContext(SucursalContext);

  // Función para calcular el precio unitario correcto según tipo de producto
  const calcularPrecioUnitario = (item) => {
    const tipoUnidadNombre = item.tipo_unidad_nombre || 'Entero';
    const esFraccionado = tipoUnidadNombre.toLowerCase() === 'fraccionado' && item.contenido_neto;
    const contenidoNeto = item.contenido_neto ? parseFloat(item.contenido_neto) : null;
    const unidadMedida = (item.ums_nombre || '').toLowerCase().trim();
    
    const esGramos = esFraccionado && (
      unidadMedida.includes('gramo') || 
      unidadMedida === 'g' || 
      unidadMedida === 'gr' ||
      unidadMedida === 'gramos' ||
      (!unidadMedida && contenidoNeto && contenidoNeto >= 100)
    ) && !unidadMedida.includes('kilo');
    
    const esKilos = esFraccionado && (
      unidadMedida.includes('kilo') || 
      unidadMedida === 'kg' ||
      unidadMedida === 'kilogramo' ||
      unidadMedida === 'kilogramos' ||
      (!unidadMedida && contenidoNeto && contenidoNeto < 10 && contenidoNeto > 0)
    );
    
    const cantidadNum = parseFloat(item.dpe_cantidad) || 0;
    const totalNum = parseFloat(item.dpe_total) || 0;
    
    if (esFraccionado && (esGramos || esKilos)) {
      // Para productos fraccionados, el precio unitario es por kilo
      // Necesitamos calcular cuántos kilos hay en la cantidad
      let cantidadEnKilos;
      if (esGramos) {
        cantidadEnKilos = cantidadNum / 1000;
      } else {
        cantidadEnKilos = cantidadNum;
      }
      // Precio unitario = total / cantidad en kilos
      return cantidadEnKilos > 0 ? totalNum / cantidadEnKilos : 0;
    } else {
      // Para productos enteros, precio unitario = total / cantidad
      return cantidadNum > 0 ? totalNum / cantidadNum : 0;
    }
  };

  useEffect(() => {
    if (pedido?.detalles) {
      setDetallesEditados(pedido.detalles.map(d => ({
        ...d,
        dpe_cantidad: Number(d.dpe_cantidad),
        dpe_total: Number(d.dpe_total),
        precioUnitario: calcularPrecioUnitario(d)
      })));
    }
  }, [pedido]);

  // Función para calcular el subtotal de un item (igual que en carrito-cliente)
  const calcularSubtotal = (item) => {
    const tipoUnidadNombre = item.tipo_unidad_nombre || 'Entero';
    const esFraccionado = tipoUnidadNombre.toLowerCase() === 'fraccionado' && item.contenido_neto;
    const contenidoNeto = item.contenido_neto ? parseFloat(item.contenido_neto) : null;
    const unidadMedida = (item.ums_nombre || '').toLowerCase().trim();
    
    const esGramos = esFraccionado && (
      unidadMedida.includes('gramo') || 
      unidadMedida === 'g' || 
      unidadMedida === 'gr' ||
      unidadMedida === 'gramos' ||
      (!unidadMedida && contenidoNeto && contenidoNeto >= 100)
    ) && !unidadMedida.includes('kilo');
    
    const esKilos = esFraccionado && (
      unidadMedida.includes('kilo') || 
      unidadMedida === 'kg' ||
      unidadMedida === 'kilogramo' ||
      unidadMedida === 'kilogramos' ||
      (!unidadMedida && contenidoNeto && contenidoNeto < 10 && contenidoNeto > 0)
    );
    
    const precioFinal = Number(item.precioUnitario) || 0;
    const cantidadNum = parseFloat(item.dpe_cantidad) || 0;
    
    if (esFraccionado && (esGramos || esKilos)) {
      // El precio unitario es por kilo
      // Convertir la cantidad a kilos para el cálculo
      let cantidadEnKilos;
      
      if (esGramos) {
        // Convertir de gramos a kilos
        cantidadEnKilos = cantidadNum / 1000;
      } else {
        // Ya está en kilos
        cantidadEnKilos = cantidadNum;
      }
      
      // Subtotal = precio por kilo × cantidad en kilos
      return precioFinal * cantidadEnKilos;
    } else {
      // Para artículos enteros, calcular normalmente
      // El precio es por unidad, así que multiplicamos por la cantidad
      return cantidadNum * precioFinal;
    }
  };

  const actualizarDetalle = (index, campo, valor) => {
    const copia = [...detallesEditados];
    copia[index][campo] = valor;

    if (campo === 'dpe_cantidad' || campo === 'precioUnitario') {
      // Usar calcularSubtotal para productos fraccionados
      copia[index].dpe_total = calcularSubtotal(copia[index]);
    }

    setDetallesEditados(copia);
  };

  const abrirDrawerAgregar = () => {
    setModoDrawer('agregar');
    setIndexAReemplazar(null);
    setNombreBusqueda('');
    setPagination({ current: 1, total: 0, pageSize: 10 });
    setDrawerVisible(true);
    buscarArticulos('', 1);
  };

  const abrirReemplazoArticulo = (index) => {
    setModoDrawer('reemplazar');
    setIndexAReemplazar(index);
    setNombreBusqueda('');
    setPagination({ current: 1, total: 0, pageSize: 10 });
    setDrawerVisible(true);
    buscarArticulos('', 1);
  };

  const eliminarDetalle = (index) => {
    const copia = [...detallesEditados];
    copia.splice(index, 1);
    setDetallesEditados(copia);
  };

  const buscarArticulos = async (nombre, page = 1) => {
    try {
      const queryParams = new URLSearchParams({
        aem_nombre: nombre,
        asu_habilitado: true,
        asu_sucursal: sucursal.suc_id,
        page: page,
        limit: pagination.pageSize
      });

      const res = await fetch(`${process.env.REACT_APP_API_URL}/articulo-sucursal/sucursal/?${queryParams.toString()}`);
      const data = await res.json();
      setArticulos(data.data || []);
      setPagination({
        current: data.pagination?.currentPage ?? page,
        total: data.pagination?.total ?? 0,
        pageSize: pagination.pageSize
      });
    } catch (err) {
      console.error('Error al buscar artículos', err);
      message.error('No se pudieron cargar artículos');
    }
  };

  const tieneDescuentoVigente = (asu) => {
    if (!asu?.asu_precio_con_descuento || !asu?.asu_fecha_d_descuento || !asu?.asu_fecha_h_descuento) return false;
    const hoy = new Date();
    const desde = new Date(asu.asu_fecha_d_descuento);
    const hasta = new Date(asu.asu_fecha_h_descuento);
    return hoy >= desde && hoy <= hasta;
  };

  const precioDeItem = (item) => {
    const asu = item?.datos_sucursal;
    if (!asu) return 0;
    return Number(tieneDescuentoVigente(asu) ? asu.asu_precio_con_descuento : asu.asu_precioventa) || 0;
  };

  const yaExisteArticulo = (aemId) => detallesEditados.some(d => Number(d.dpe_articulo) === Number(aemId));

  const agregarArticuloDesdeDrawer = (nuevoArticulo) => {
    const aemId = nuevoArticulo.articulo_empresa.aem_id;

    if (yaExisteArticulo(aemId)) {
      message.warning('Ese artículo ya está en el pedido');
      return;
    }

    const precio = precioDeItem(nuevoArticulo);
    const tipoUnidadNombre = nuevoArticulo.articulo_empresa?.tipo_unidad_nombre || 'Entero';
    const contenidoNeto = nuevoArticulo.articulo_empresa?.detalle?.contenido_neto 
      ? parseFloat(nuevoArticulo.articulo_empresa.detalle.contenido_neto) 
      : null;
    const ums_nombre = nuevoArticulo.articulo_empresa?.detalle?.ums_nombre || null;
    const esFraccionado = tipoUnidadNombre.toLowerCase() === 'fraccionado' && contenidoNeto;
    
    // Para productos fraccionados, cantidad inicial = contenido_neto
    const cantidadInicial = esFraccionado ? contenidoNeto : 1;
    
    // El precio unitario es el precio por kilo para fraccionados, o por unidad para enteros
    const precioUnitario = precio;

    const nuevo = {
      dpe_id: undefined, // nuevo
      dpe_articulo: aemId,
      aem_nombre: nuevoArticulo.articulo_empresa.aem_nombre,
      dpe_cantidad: cantidadInicial,
      precioUnitario: precioUnitario,
      tipo_unidad_nombre: tipoUnidadNombre,
      contenido_neto: contenidoNeto,
      ums_nombre: ums_nombre,
      dpe_total: calcularSubtotal({
        dpe_cantidad: cantidadInicial,
        precioUnitario: precioUnitario,
        tipo_unidad_nombre: tipoUnidadNombre,
        contenido_neto: contenidoNeto,
        ums_nombre: ums_nombre
      })
    };

    setDetallesEditados(prev => [...prev, nuevo]);
    setDrawerVisible(false);
  };

  const reemplazarArticulo = (nuevoArticulo) => {
    const nuevoId = nuevoArticulo.articulo_empresa.aem_id;

    const yaExiste = detallesEditados.some((d, i) =>
      Number(d.dpe_articulo) === Number(nuevoId) && i !== indexAReemplazar
    );
    if (yaExiste) {
      message.warning('Ese artículo ya está en el pedido');
      return;
    }

    const copia = [...detallesEditados];
    const precio = precioDeItem(nuevoArticulo);
    const tipoUnidadNombre = nuevoArticulo.articulo_empresa?.tipo_unidad_nombre || 'Entero';
    const contenidoNeto = nuevoArticulo.articulo_empresa?.detalle?.contenido_neto 
      ? parseFloat(nuevoArticulo.articulo_empresa.detalle.contenido_neto) 
      : null;
    const ums_nombre = nuevoArticulo.articulo_empresa?.detalle?.ums_nombre || null;
    const cantidadActual = Number(copia[indexAReemplazar].dpe_cantidad || 1);

    copia[indexAReemplazar] = {
      ...copia[indexAReemplazar],
      dpe_articulo: nuevoId,
      aem_nombre: nuevoArticulo.articulo_empresa.aem_nombre,
      precioUnitario: precio,
      tipo_unidad_nombre: tipoUnidadNombre,
      contenido_neto: contenidoNeto,
      ums_nombre: ums_nombre,
      dpe_total: calcularSubtotal({
        dpe_cantidad: cantidadActual,
        precioUnitario: precio,
        tipo_unidad_nombre: tipoUnidadNombre,
        contenido_neto: contenidoNeto,
        ums_nombre: ums_nombre
      })
    };

    setDetallesEditados(copia);
    setDrawerVisible(false);
  };

  const columnas = [
    {
      title: 'Artículo',
      dataIndex: 'aem_nombre',
      render: (nombre, _, index) => (
        <Space direction="vertical" size={4}>
          <Text>{nombre}</Text>
          <Button
            size="small"
            icon={<SwapOutlined />}
            onClick={() => abrirReemplazoArticulo(index)}
          >
            Reemplazar
          </Button>
        </Space>
      )
    },
    {
      title: 'Cantidad',
      dataIndex: 'dpe_cantidad',
      width: 140,
      render: (cantidad, record, index) => {
        const tipoUnidadNombre = record.tipo_unidad_nombre || 'Entero';
        const esFraccionado = tipoUnidadNombre.toLowerCase() === 'fraccionado' && record.contenido_neto;
        const contenidoNeto = record.contenido_neto ? parseFloat(record.contenido_neto) : null;
        const unidadMedida = (record.ums_nombre || '').toLowerCase().trim();
        
        const esGramos = esFraccionado && (
          unidadMedida.includes('gramo') || 
          unidadMedida === 'g' || 
          unidadMedida === 'gr' ||
          unidadMedida === 'gramos' ||
          (!unidadMedida && contenidoNeto && contenidoNeto >= 100)
        ) && !unidadMedida.includes('kilo');
        
        const esKilos = esFraccionado && (
          unidadMedida.includes('kilo') || 
          unidadMedida === 'kg' ||
          unidadMedida === 'kilogramo' ||
          unidadMedida === 'kilogramos' ||
          (!unidadMedida && contenidoNeto && contenidoNeto < 10 && contenidoNeto > 0)
        );
        
        // Convertir cantidad a kilos si es necesario para mostrar
        const convertirAKilos = (cant) => {
          if (esFraccionado) {
            if (esGramos) {
              const totalKilos = cant / 1000;
              return parseFloat(totalKilos.toFixed(2));
            } else if (esKilos) {
              return parseFloat(cant.toFixed(1));
            } else if (!unidadMedida && contenidoNeto) {
              if (cant >= 100) {
                const totalKilos = cant / 1000;
                return parseFloat(totalKilos.toFixed(2));
              } else if (cant < 10 && cant > 0) {
                return parseFloat(cant.toFixed(1));
              }
            }
          }
          return cant;
        };
        
        const cantidadNum = parseFloat(cantidad) || 0;
        // Para mostrar, convertir a kilos si es fraccionado
        const cantidadMostrar = esFraccionado && (esGramos || esKilos || (!unidadMedida && contenidoNeto))
          ? convertirAKilos(cantidadNum) 
          : cantidadNum;
        
        const mostrarKg = esFraccionado && (esGramos || esKilos || (!unidadMedida && contenidoNeto && (cantidadNum >= 100 || (cantidadNum < 10 && cantidadNum > 0))));
        const cantidadMinimaMostrar = esFraccionado && mostrarKg 
          ? (esGramos ? contenidoNeto / 1000 : contenidoNeto)
          : (esFraccionado ? contenidoNeto : 1);
        
        // Función para convertir de vuelta desde kilos a la unidad original
        const convertirDesdeKilos = (kilos) => {
          if (esFraccionado) {
            if (esGramos) {
              return kilos * 1000;
            } else if (esKilos) {
              return kilos;
            } else if (!unidadMedida && contenidoNeto) {
              // Inferir: si contenido_neto >= 100, probablemente gramos
              if (contenidoNeto >= 100) {
                return kilos * 1000;
              } else {
                return kilos;
              }
            }
          }
          return kilos;
        };
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <InputNumber
              min={cantidadMinimaMostrar}
              step={mostrarKg ? 0.1 : (esFraccionado ? contenidoNeto : 1)}
              precision={mostrarKg ? (esKilos ? 1 : 2) : 0}
              value={mostrarKg ? cantidadMostrar : cantidad}
              onChange={val => {
                // Si estamos mostrando en kilos, convertir de vuelta a la unidad original
                const valorFinal = mostrarKg ? convertirDesdeKilos(val) : val;
                actualizarDetalle(index, 'dpe_cantidad', valorFinal);
              }}
              style={{ width: '100px' }}
            />
            {mostrarKg && (
              <span style={{ fontSize: '0.9rem', color: '#666' }}>kg</span>
            )}
          </div>
        );
      },
    },
    {
      title: 'Precio Unitario',
      dataIndex: 'precioUnitario',
      width: 160,
      render: (precio, record, index) => {
        const tipoUnidadNombre = record.tipo_unidad_nombre || 'Entero';
        const esFraccionado = tipoUnidadNombre.toLowerCase() === 'fraccionado' && record.contenido_neto;
        const unidadMedida = (record.ums_nombre || '').toLowerCase().trim();
        const contenidoNeto = record.contenido_neto ? parseFloat(record.contenido_neto) : null;
        
        const esGramos = esFraccionado && (
          unidadMedida.includes('gramo') || 
          unidadMedida === 'g' || 
          unidadMedida === 'gr' ||
          unidadMedida === 'gramos' ||
          (!unidadMedida && contenidoNeto && contenidoNeto >= 100)
        ) && !unidadMedida.includes('kilo');
        
        const esKilos = esFraccionado && (
          unidadMedida.includes('kilo') || 
          unidadMedida === 'kg' ||
          unidadMedida === 'kilogramo' ||
          unidadMedida === 'kilogramos' ||
          (!unidadMedida && contenidoNeto && contenidoNeto < 10 && contenidoNeto > 0)
        );
        
        const mostrarKg = esFraccionado && (esGramos || esKilos);
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span>${Number(precio || 0).toFixed(2)}</span>
            {mostrarKg && (
              <span style={{ fontSize: '0.9rem', color: '#666' }}>/kg</span>
            )}
          </div>
        );
      },
    },
    {
      title: 'Total',
      dataIndex: 'dpe_total',
      render: total => `$${Number(total || 0).toFixed(2)}`
    },
    {
      title: '',
      width: 60,
      render: (_, __, index) => (
        <Popconfirm
          title="¿Eliminar este artículo?"
          onConfirm={() => eliminarDetalle(index)}
          okText="Sí"
          cancelText="No"
        >
          <Button icon={<DeleteOutlined />} danger size="small" />
        </Popconfirm>
      )
    }
  ];

  const totalPedido = detallesEditados.reduce((sum, item) => sum + (Number(item.dpe_total) || 0), 0);

  const handleGuardar = async () => {
    if (saving) return; // Prevenir múltiples clics

    if (detallesEditados.length === 0) {
      message.error('Debe haber al menos un artículo en el pedido');
      return;
    }

    const tieneError = detallesEditados.some(
      d => Number(d.dpe_cantidad) <= 0 || Number(d.dpe_total) <= 0 || !d.dpe_articulo
    );

    if (tieneError) {
      message.error('Hay artículos con cantidad o total inválido');
      return;
    }

    // prevenir duplicados por si acaso
    const ids = detallesEditados.map(d => Number(d.dpe_articulo));
    const setIds = new Set(ids);
    if (setIds.size !== ids.length) {
      message.error('No se permiten artículos repetidos');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/pedido/${pedido.ped_id}/editar`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.token}`
        },
        body: JSON.stringify({ detalles: detallesEditados })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al actualizar');

      message.success('Pedido actualizado correctamente');
      onGuardarCambios?.();
      onClose?.();
    } catch (err) {
      console.error(err.message);
      message.error('Error al guardar cambios');
    } finally {
      setSaving(false);
    }
  };

  const rowKey = (row, idx) => `${row.dpe_id ?? 'new'}-${row.dpe_articulo}-${idx}`;

  return (
    <>
      <Modal
        open={visible}
        title={`Editar pedido #${pedido?.ped_id}`}
        onCancel={saving ? undefined : onClose}
        onOk={handleGuardar}
        okText="Guardar"
        cancelText="Cancelar"
        width={900}
        confirmLoading={saving}
        okButtonProps={{ disabled: saving }}
        cancelButtonProps={{ disabled: saving }}
      >
        <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
          <Col>
            <Button type="primary" icon={<PlusOutlined />} onClick={abrirDrawerAgregar}>
              Agregar artículo
            </Button>
          </Col>
          <Col>
            <Text strong>Total del pedido: ${totalPedido.toFixed(2)}</Text>
          </Col>
        </Row>

        <Table
          dataSource={detallesEditados}
          columns={columnas}
          rowKey={rowKey}
          pagination={false}
        />
      </Modal>

      <Drawer
        title={modoDrawer === 'agregar' ? 'Agregar artículo' : 'Reemplazar artículo'}
        placement="right"
        width={420}
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
      >
        <Input.Search
          placeholder="Buscar artículo"
          enterButton={<SearchOutlined />}
          value={nombreBusqueda}
          onChange={(e) => setNombreBusqueda(e.target.value)}
          onSearch={(val) => {
            setNombreBusqueda(val);
            buscarArticulos(val, 1);
          }}
          allowClear
        />
        <Divider />
        <List
          itemLayout="horizontal"
          dataSource={articulos}
          renderItem={(item) => {
            const aem = item.articulo_empresa;
            const precio = precioDeItem(item);

            const onSelect = () => {
              if (modoDrawer === 'agregar') {
                agregarArticuloDesdeDrawer(item);
              } else {
                reemplazarArticulo(item);
              }
            };

            return (
              <List.Item
                onClick={onSelect}
                style={{ cursor: 'pointer' }}
                actions={[
                  <Button type="link" onClick={onSelect}>
                    {modoDrawer === 'agregar' ? 'Agregar' : 'Reemplazar'}
                  </Button>
                ]}
              >
                <List.Item.Meta
                  avatar={<Avatar src={aem.aem_imagen} />}
                  title={aem.aem_nombre}
                  description={`$${Number(precio).toFixed(2)}`}
                />
              </List.Item>
            );
          }}
        />
        {pagination.total > 0 && (
          <Pagination
            current={pagination.current}
            total={pagination.total}
            pageSize={pagination.pageSize}
            onChange={(page) => buscarArticulos(nombreBusqueda, page)}
            style={{ marginTop: '1rem', textAlign: 'center' }}
            showSizeChanger={false}
          />
        )}
      </Drawer>
    </>
  );
};

export default CEditarPedido;